import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { IImportJob, IOperationResultNoValue } from '../../../../../extViewmodels';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { openDownloadUrl } from '../../../../../models/UiUtils';
import {
	AccountImportsViewModel,
	AccountOperationsViewModel,
	AdminPermission,
} from '../../../../../viewmodels/AdminViewModels';
import {
	resolveRevertError,
	RevertResultModal,
	type RevertResultState,
	revertResultFromOperation,
} from '../../../../components/RevertResultModal';
import { Checkbox } from '../../../../../web/components/Checkbox';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { ImportJobAccount } from '../../../../components/ImportJobAccount';
import { styleSheet } from './styles';

interface Props {
	accountVM: AccountOperationsViewModel;
}

const COLUMN_COUNT = 6;

function _Imports({ accountVM }: Props) {
	const userSession = useUserSession();
	const history = useHistory();
	const errorMessages = useErrorMessages();
	const [importsVM] = useState(new AccountImportsViewModel(userSession, accountVM.account));
	const [showRevertedJobs, setShowRevertedJobs] = useState(false);
	const [selectedJobId, setSelectedJobId] = useState<string | undefined>();
	const [forceJobId, setForceJobId] = useState('');
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [showForceConfirmation, setShowForceConfirmation] = useState(false);
	const [revertResultState, setRevertResultState] = useState<RevertResultState | null>(null);
	useEffect(() => {
		importsVM.loadImports()?.catch(error => {
			errorMessages.pushApiError(error);
		});
	}, [importsVM, errorMessages]);
	if (importsVM.isLoading) {
		return (
			<div className={`${css(styleSheet.container)}`}>
				<LoadingSpinner type='large' className={css(styleSheet.spinnerBuffer)} />
			</div>
		);
	}
	const onRefreshDataClicked = () => {
		history.push(`/accounts/${accountVM.account.id}/import/data-refresh`);
	};
	const onShowRevertedChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setShowRevertedJobs(e.target.checked);
		importsVM.loadImports(e.target.checked)?.catch(err => {
			errorMessages.pushApiError(err);
		});
	};
	const onRevertClick = (jobId: string) => () => {
		setIsConfirmModalOpen(true);
		setSelectedJobId(jobId);
	};
	const onForceRevertImport = (jobId: string) => () => {
		setForceJobId(jobId);
		setShowForceConfirmation(true);
	};
	const downloadFileOnClick = (jobId: string, fileName: string) => async () => {
		const url = await importsVM.downloadImportFile(jobId);
		openDownloadUrl(url, fileName);
	};
	const onDeleteConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (!!result && result.isDestructive && !canceled && !!selectedJobId) {
			const jobId = selectedJobId;
			importsVM
				.revertImport(jobId)
				.then(revert => {
					setRevertResultState(revertResultFromOperation(revert));
					setSelectedJobId('');
					setForceJobId('');
				})
				.catch((err: IOperationResultNoValue) => {
					const resolution = resolveRevertError(err);
					if (resolution.showInModal) {
						setRevertResultState(resolution.state);
					} else {
						errorMessages.pushApiError(err);
					}
					setForceJobId(jobId);
					setSelectedJobId('');
				});
		}
		setIsConfirmModalOpen(false);
	};
	const onForceRevertConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (!!result && !canceled && !!result.representedObject && !!forceJobId) {
			importsVM
				.forceRevertImport(forceJobId)
				.then(revert => {
					setRevertResultState(revertResultFromOperation(revert));
				})
				.catch((err: IOperationResultNoValue) => {
					const resolution = resolveRevertError(err);
					if (resolution.showInModal) {
						setRevertResultState(resolution.state);
					} else {
						errorMessages.pushApiError(err);
					}
				});
		}
		setForceJobId('');
		setShowForceConfirmation(false);
	};
	const onRevertResultClose = () => {
		setRevertResultState(null);
	};
	const onViewRevertSummary = (importJob: IImportJob) => () => {
		if (!importJob.revertSummary) return;
		setRevertResultState(revertResultFromOperation({ success: true, value: importJob.revertSummary }));
	};

	return (
		<div className={`${css(styleSheet.container)}`}>
			<div>
				<div className={css(styleSheet.dataRefreshSection)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={onRefreshDataClicked}>
						<span>Refresh Insurance Data</span>
					</button>
					<div className={css(styleSheet.dataRefreshText)}>
						Have new policy data to import? Rather than reverting
						<br />
						previous uploads, try the data refresh flow!
					</div>
				</div>
				<ConditionalAccess permission={AdminPermission.OnlyForDevelopers}>
					<div className={css(styleSheet.revertCheckbox)}>
						<Checkbox id='import-reverted-cvs-checkbox' onChange={onShowRevertedChanged} checked={showRevertedJobs}>
							<span>Show reverted imports</span>
						</Checkbox>
					</div>
				</ConditionalAccess>
				<table className={css(styleSheet.table)}>
					<thead className={css(styleSheet.tableHead)}>
						<tr>
							<th className={css(styleSheet.th, styleSheet.colDate)}>Date</th>
							<th className={css(styleSheet.th, styleSheet.colUser)}>User</th>
							<th className={css(styleSheet.th, styleSheet.colFileName)}>File Name</th>
							<th className={css(styleSheet.th, styleSheet.colProcessed)}>Processed</th>
							<th className={css(styleSheet.th, styleSheet.colJobId)}>Job ID</th>
							<th className={css(styleSheet.th, styleSheet.colAction)}>{showRevertedJobs ? 'Status' : 'Action'}</th>
						</tr>
					</thead>
					<tbody>
						{importsVM.isLoading ? (
							<tr>
								<td className={css(styleSheet.td, styleSheet.loadingRow)} colSpan={COLUMN_COUNT}>
									<LoadingSpinner type='large' className={css(styleSheet.spinnerBuffer)} />
								</td>
							</tr>
						) : importsVM.accountImports.length ? (
							importsVM.accountImports.map(importJob => (
								<ImportJobAccount
									key={importJob.id}
									importJob={importJob}
									isReverting={importsVM.isReverting && (selectedJobId === importJob.id || forceJobId === importJob.id)}
									revertImport={onRevertClick(importJob.id)}
									onForceRevertImport={forceJobId === importJob.id ? onForceRevertImport(importJob.id) : null}
									onViewRevertSummary={onViewRevertSummary(importJob)}
									showButton={!showRevertedJobs}
									downloadFile={downloadFileOnClick(importJob.id, importJob.additionalFields?.importFileName)}
									columnCount={COLUMN_COUNT}
								/>
							))
						) : (
							<tr>
								<td className={css(styleSheet.td, styleSheet.noneFound)} colSpan={COLUMN_COUNT}>
									No imports found
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isConfirmModalOpen,
					onRequestClose: onDeleteConfirmationRequestClose,
				}}
				options={DefaultDeleteConfirmationOptions}
				title='Reverting this import will delete any contacts created by the import. Are you sure?'
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showForceConfirmation,
					onRequestClose: onForceRevertConfirmationRequestClose,
				}}
				options={DefaultDeleteConfirmationOptions}
				title='Force reverting this import will permanently delete any contacts created during the import, even if they were not part of the imported file. Are you absolutely sure?'
			/>
			<RevertResultModal
				isOpen={revertResultState !== null}
				onRequestClose={onRevertResultClose}
				state={revertResultState}
			/>
		</div>
	);
}

export const Imports = observer(_Imports);
